import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Container,
  Flex,
  Heading,
  Section,
  Text,
  Button,
  Callout,
} from '@/components/ui';
import { toast } from '@/utils/toast';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconLoader2,
} from '@tabler/icons-react';

const toastTypes = [
  { type: 'success', label: 'Success', icon: IconCheck },
  { type: 'error', label: 'Error', icon: IconX },
  { type: 'warning', label: 'Warning', icon: IconAlertTriangle },
  { type: 'info', label: 'Info', icon: IconInfoCircle },
  { type: 'loading', label: 'Loading', icon: IconLoader2 },
] as const;

const variants = ['solid', 'soft', 'surface', 'outlined', 'ghost'] as const;
const sizes = ['xsmall', 'small', 'medium', 'large'] as const;

export const ToastDemoPage: React.FC = () => {
  const showToast = (
    type: 'success' | 'error' | 'warning' | 'info' | 'loading',
    message?: string,
    description?: string,
    persistent: boolean = true
  ) => {
    const defaultMessages = {
      success: 'Operation completed successfully!',
      error: 'An error occurred',
      warning: 'Warning: Please review this',
      info: 'Information message',
      loading: 'Processing your request...',
    };

    const defaultDescriptions = {
      success: 'Your changes have been saved successfully.',
      error: 'Please check your input and try again.',
      warning: 'This action may have unintended consequences.',
      info: 'You can find more details in the documentation.',
      loading: 'Please wait while we process your request.',
    };

    const toastMessage = message || defaultMessages[type];
    const toastDescription = description || defaultDescriptions[type];
    
    // Use Infinity duration for persistent toasts (for demo page)
    const duration = persistent ? Infinity : undefined;

    switch (type) {
      case 'success':
        toast.success(toastMessage, { 
          description: toastDescription,
          duration,
        });
        break;
      case 'error':
        toast.error(toastMessage, { 
          description: toastDescription,
          duration,
        });
        break;
      case 'warning':
        toast.warning(toastMessage, { 
          description: toastDescription,
          duration,
        });
        break;
      case 'info':
        toast.info(toastMessage, { 
          description: toastDescription,
          duration,
        });
        break;
      case 'loading':
        {
          const toastId = toast.loading(toastMessage, {
            description: toastDescription,
            duration,
          });
          // Only auto-dismiss loading toast if not persistent
          if (!persistent) {
            setTimeout(() => {
              toast.dismiss(toastId);
              toast.success('Processing completed!', {
                description: 'All operations finished successfully.',
                duration: Infinity,
              });
            }, 3000);
          }
          break;
        }
    }
  };

  return (
    <Container pt="4" pb="4" size="expand">
      <Section>
        <Flex direction="column" gap="6">
          <div>
            <Heading size="large" className="mb-2">
              Toast Component Demo
            </Heading>
            <Text size="2" color="gray">
              Toast notifications with styles inherited from Callout component.
              Supports multiple variants, sizes, and colors. Toasts are displayed
              using Sonner library with custom styling.
            </Text>
          </div>

          {/* Basic Toast Types */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Toast Types</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Callout
                  icon={<IconInfoCircle />}
                  title="Demo Mode"
                  color="info"
                  size="small"
                >
                  Toasts on this page are set to persist (not auto-close) so you
                  can inspect their styles. Use the "Dismiss All" button to close
                  all toasts.
                </Callout>
                <Flex gap="2" wrap="wrap" align="center">
                  {toastTypes.map(({ type, label, icon: Icon }) => (
                    <Button
                      key={type}
                      size="small"
                      variant="soft"
                      color={type === 'loading' ? 'default' : type}
                      onClick={() => showToast(type)}
                      startIcon={<Icon size={16} />}
                    >
                      Show {label} Toast
                    </Button>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    color="default"
                    onClick={() => toast.dismiss()}
                  >
                    Dismiss All
                  </Button>
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Toast Variants Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Toast Variants (Reference)</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Toast styles support all Alert variants. Here are Callout
                  examples showing how each variant looks (toasts inherit these
                  styles):
                </Text>
                {variants.map(variant => (
                  <div key={variant}>
                    <Text size="3" weight="medium" mb="2">
                      Variant: {variant}
                    </Text>
                    <Flex direction="column" gap="2">
                      {toastTypes
                        .filter(t => t.type !== 'loading')
                        .map(({ type, icon: Icon }) => (
                          <Callout
                            key={type}
                            icon={<Icon size={16} />}
                            title={`${type.charAt(0).toUpperCase() + type.slice(1)} Toast`}
                            color={type}
                            size="small"
                          >
                            This is how a {type} toast looks with {variant}{' '}
                            variant
                          </Callout>
                        ))}
                    </Flex>
                  </div>
                ))}
              </Flex>
            </CardContent>
          </Card>

          {/* Toast Sizes Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Toast Sizes (Reference)</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Toast styles support all Callout sizes. Here are examples
                  showing how each size looks:
                </Text>
                <Flex direction="column" gap="2">
                  {sizes.map(size => (
                    <Callout
                      key={size}
                      icon={<IconInfoCircle size={16} />}
                      title={`Size: ${size}`}
                      color="info"
                      size={size}
                    >
                      This is a toast with {size} size
                    </Callout>
                  ))}
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Toast Colors Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Toast Colors</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Toast supports the following colors. Click buttons to see them
                  in action:
                </Text>
                <Flex direction="column" gap="2">
                  {toastTypes.map(({ type, label, icon: Icon }) => (
                    <Flex key={type} gap="2" align="center">
                      <Button
                        size="small"
                        variant="soft"
                        color={type === 'loading' ? 'default' : type}
                        onClick={() => showToast(type)}
                        startIcon={<Icon size={16} />}
                      >
                        Show {label}
                      </Button>
                      <Callout
                        icon={<Icon size={16} />}
                        title={`${label} Toast`}
                        color={type === 'loading' ? 'default' : type}
                        size="small"
                      >
                        Example of {label.toLowerCase()} toast styling
                      </Callout>
                    </Flex>
                  ))}
                </Flex>
                <Flex gap="2" mt="2">
                  <Button
                    size="small"
                    variant="outlined"
                    color="default"
                    onClick={() => toast.dismiss()}
                  >
                    Dismiss All Toasts
                  </Button>
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Advanced Examples */}
          <Card>
            <CardHeader>
              <CardTitle>Advanced Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Examples of toasts with custom messages and descriptions:
                </Text>
                <Flex gap="2" wrap="wrap">
                  <Button
                    size="small"
                    variant="soft"
                    onClick={() => {
                      toast.success('File uploaded successfully!', {
                        description:
                          'Your file "document.pdf" has been uploaded and is ready for processing.',
                        duration: Infinity,
                      });
                    }}
                  >
                    Long Description Toast
                  </Button>
                  <Button
                    size="small"
                    variant="soft"
                    onClick={() => {
                      toast.error('Validation failed', {
                        description:
                          'Please check the following fields: Email, Password, Confirm Password',
                        duration: Infinity,
                      });
                    }}
                  >
                    Error with Details
                  </Button>
                  <Button
                    size="small"
                    variant="soft"
                    onClick={() => {
                      const id = toast.loading('Uploading files...', {
                        description: '3 of 5 files uploaded',
                        duration: Infinity,
                      });
                      setTimeout(() => {
                        toast.dismiss(id);
                        toast.success('All files uploaded!', {
                          description: '5 files have been successfully uploaded.',
                          duration: Infinity,
                        });
                      }, 3000);
                    }}
                  >
                    Loading → Success
                  </Button>
                  <Button
                    size="small"
                    variant="soft"
                    onClick={() => {
                      toast.success('First toast', { duration: Infinity });
                      setTimeout(() => toast.info('Second toast', { duration: Infinity }), 500);
                      setTimeout(() => toast.warning('Third toast', { duration: Infinity }), 1000);
                      setTimeout(() => toast.error('Fourth toast', { duration: Infinity }), 1500);
                    }}
                  >
                    Multiple Toasts
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="default"
                    onClick={() => toast.dismiss()}
                  >
                    Dismiss All
                  </Button>
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Implementation Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Implementation Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="3">
                <Callout
                  icon={<IconInfoCircle />}
                  title="Styling Architecture"
                  color="info"
                  size="small"
                >
                  Toast styles are inherited from Callout component. All variants
                  (solid, soft, surface, outlined, ghost), sizes (xsmall,
                  small, medium, large), and colors (success, error, warning,
                  info) are supported through CSS variables and data-attributes.
                </Callout>
                <Callout
                  icon={<IconInfoCircle />}
                  title="Default Configuration"
                  color="info"
                  size="small"
                >
                  By default, toasts use soft variant and small size. Variants
                  and sizes can be customized through data-attributes on Sonner
                  toast elements if needed in the future.
                </Callout>
                <Callout
                  icon={<IconInfoCircle />}
                  title="Usage"
                  color="info"
                  size="small"
                >
                  Use the toast utility from @/utils/toast to show toasts:
                  toast.success(), toast.error(), toast.warning(), toast.info(),
                  toast.loading()
                </Callout>
              </Flex>
            </CardContent>
          </Card>
        </Flex>
      </Section>
    </Container>
  );
};

export default ToastDemoPage;

